type AnalyticsParams = Record<string, string | number | boolean | null | undefined>;

declare global {
  interface Window {
    dataLayer: unknown[];
    gtag?: (...args: unknown[]) => void;
    clarity?: (...args: unknown[]) => void;
  }
}

let gaInitialized = false;
let clarityInitialized = false;

const getGaMeasurementId = (): string => (process.env.REACT_APP_GA_MEASUREMENT_ID || '').trim();
const getClarityProjectId = (): string => (process.env.REACT_APP_CLARITY_PROJECT_ID || '').trim();

export const initGoogleAnalytics = (): void => {
  const measurementId = getGaMeasurementId();

  if (!measurementId || gaInitialized || typeof document === 'undefined') {
    return;
  }

  if (!document.getElementById('ga4-script')) {
    const script = document.createElement('script');
    script.id = 'ga4-script';
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    document.head.appendChild(script);
  }

  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function gtagPush(...args: unknown[]) {
    window.dataLayer.push(args);
  };

  window.gtag('js', new Date());
  window.gtag('config', measurementId, {
    send_page_view: false,
    anonymize_ip: true,
  });

  gaInitialized = true;
};

export const initClarity = (): void => {
  const projectId = getClarityProjectId();

  if (!projectId || clarityInitialized || typeof document === 'undefined') {
    return;
  }

  // Match Clarity's official bootstrap behavior by defining a queue function
  // before loading the external script.
  if (typeof window.clarity !== 'function') {
    const clarityQueue = (...args: unknown[]) => {
      ((clarityQueue as unknown as { q?: unknown[] }).q = (clarityQueue as unknown as { q?: unknown[] }).q || []).push(args);
    };
    window.clarity = clarityQueue;
  }

  if (!document.getElementById('clarity-script')) {
    const script = document.createElement('script');
    script.id = 'clarity-script';
    script.async = true;
    script.src = `https://www.clarity.ms/tag/${projectId}`;
    document.head.appendChild(script);
  }

  clarityInitialized = true;
};

export const initializeAnalytics = (): void => {
  initGoogleAnalytics();
  initClarity();
};

export const trackPageView = (path: string): void => {
  const measurementId = getGaMeasurementId();

  if (!measurementId || typeof window.gtag !== 'function') {
    return;
  }

  window.gtag('event', 'page_view', {
    page_path: path,
    page_location: window.location.href,
    page_title: document.title,
  });
};

export const trackEvent = (eventName: string, params: AnalyticsParams = {}): void => {
  const measurementId = getGaMeasurementId();

  if (!measurementId || typeof window.gtag !== 'function') {
    return;
  }

  window.gtag('event', eventName, params);
};
